import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, DestroyRef, ElementRef, inject, linkedSignal, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, Subscription } from 'rxjs';
import { BreadcrumbComponent } from '../../../../shared/components/breadcrumb/breadcrumb.component';
import { LoginCardComponent } from '../../../../shared/components/login-card/login-card.component';
import { PaginacaoComponent } from '../../../../shared/components/paginacao/paginacao.component';
import { LucideModule } from '../../../../shared/modules/lucide.module';
import {
  CATEGORIAS_SOLICITACAO,
  CategoriaSolicitacao,
  formatarData,
  Solicitacao,
  SolicitacoesService,
  STATUS_SOLICITACAO,
  StatusSolicitacao,
} from '../../services/solicitacoes.service';
import { DetalheSolicitacaoComponent } from './detalhe-solicitacao/detalhe-solicitacao.component';
import { PREFIXO_PROTOCOLO_SERVICO } from '../../models/solicitacao-servico.model';
import { CatalogoPublicoService } from '../../services/catalogo-publico.service';
import {
  SolicitacaoServicoPublicaService,
  mapearErroSolicitacao,
  paraSolicitacaoAcompanhamento,
} from '../../services/solicitacao-servico-publica.service';
import { paraSolicitacaoSic, PREFIXO_PROTOCOLO_SIC } from '../../models/pedido-sic-acompanhamento.model';
import { mapearErroConsultaSic, PedidoSicAcompanhamentoService } from '../../services/pedido-sic-acompanhamento.service';
import { CaptchaConsulta, CaptchaConsultaService, RespostaCaptcha } from '../../services/captcha-consulta.service';

interface Filtros {
  protocolo: string;
  categoria: string;
  servicos: readonly string[];
  status: string;
  dataInicial: string;
  dataFinal: string;
}

const FILTROS_VAZIOS: Filtros = {
  protocolo: '',
  categoria: '',
  servicos: [],
  status: '',
  dataInicial: '',
  dataFinal: '',
};

/** Cada aplicação recebe cópias próprias da lista para rascunho e filtro não se enroscarem. */
function clonarFiltros(filtros: Filtros): Filtros {
  return { ...filtros, servicos: [...filtros.servicos] };
}

const LARGURA_PDF = 900;

type EstadoCaptcha = 'carregando' | 'pronto' | 'falha';

function formatarCpf(valor: string): string {
  const digitos = valor.replace(/\D/g, '').slice(0, 11);

  return digitos
    .replace(/^(\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/^(\d{3})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3-$4');
}
 
@Component({
  selector: 'app-acompanhar-solicitacao-deslogado',
  templateUrl: './acompanhar-solicitacao-deslogado.component.html',
  styleUrls: ['./acompanhar-solicitacao-deslogado.component.css'],
  imports: [
    BreadcrumbComponent,
    DetalheSolicitacaoComponent,
    LoginCardComponent,
    LucideModule,
    NgTemplateOutlet,
    PaginacaoComponent,
  ],
})
export class AcompanharSolicitacaoDeslogadoComponent {
  private readonly solicitacoesService = inject(SolicitacoesService);
  private readonly solicitacaoServicoPublica = inject(SolicitacaoServicoPublicaService);
  private readonly pedidoSic = inject(PedidoSicAcompanhamentoService);
  private readonly catalogo = inject(CatalogoPublicoService);
  private readonly captchaConsulta = inject(CaptchaConsultaService);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * O que a API devolveu: a solicitação de SERVIÇO (protocolo `CSE-`) ou o pedido do e-SIC
   * (`SIC-`), no mesmo modelo de tela. Os demais protocolos continuam no caminho de antes.
   */
  private readonly resultadoApi = signal<Solicitacao | null>(null);

  /** Consulta à API no ar — o botão fica bloqueado e anuncia a espera. */
  readonly consultandoApi = signal(false);

  /** Falha de rede ou indisponibilidade na consulta à API (não o 404). */
  readonly erroConsulta = signal('');
  private readonly modalDetalhe = viewChild<ElementRef<HTMLDialogElement>>('modalDetalhe');
  private readonly modalServicos = viewChild<ElementRef<HTMLDialogElement>>('modalServicos');
  private readonly detalhePdf = viewChild('detalhePdf', { read: ElementRef<HTMLElement> });
  private readonly campoCaptcha = viewChild<ElementRef<HTMLInputElement>>('campoCaptcha');

  readonly gerandoPdf = signal(false);

  readonly statusDisponiveis = STATUS_SOLICITACAO;
  readonly categoriasDisponiveis = CATEGORIAS_SOLICITACAO;

  /**
   * Os serviços do modal "Serviços vinculados", vindos do catálogo público do órgão. O filtro
   * compara pelo nome, que é o que a consulta por protocolo devolve.
   */
  readonly servicos = this.catalogo.nomesServicos;
  readonly carregandoServicos = this.catalogo.carregandoServicos;
  readonly falhouServicos = this.catalogo.falhouServicos;

 
  readonly protocolo = signal('');
  readonly cpf = signal('');
  readonly captchaDigitado = signal('');
  readonly tentouConsultar = signal(false);

  /**
   * O captcha emitido pela API (change captcha-consulta-protocolo): o token e a imagem. O código
   * só existe na imagem — a tela não o conhece e não o confere; quem confere é a consulta.
   */
  readonly captcha = signal<CaptchaConsulta | null>(null);
  readonly estadoCaptcha = signal<EstadoCaptcha>('carregando');

  /** A recusa da API: o código não conferiu ou venceu, e uma imagem nova já foi pedida. */
  readonly erroCaptchaApi = signal('');

  readonly imagemCaptcha = computed(() => {
    const captcha = this.captcha();
    return captcha ? `data:image/png;base64,${captcha.imagem}` : '';
  });

  private carregamentoCaptcha?: Subscription;

  readonly erroProtocolo = computed(() =>
    this.tentouConsultar() && !this.protocolo().trim() ? 'Informe o número do protocolo.' : ''
  );

  /** O CPF é opcional: só é conferido quando a pessoa opta por informá-lo. */
  readonly erroCpf = computed(() => {
    if (!this.tentouConsultar()) return '';

    const digitos = this.cpf().replace(/\D/g, '');
    if (!digitos) return '';

    return digitos.length === 11 ? '' : 'O CPF deve ter 11 dígitos.';
  });

  /** No navegador só o preenchimento é conferido; a igualdade do código é com a API. */
  readonly erroCaptcha = computed(() => {
    if (this.erroCaptchaApi()) return this.erroCaptchaApi();
    if (!this.tentouConsultar()) return '';

    if (this.estadoCaptcha() !== 'pronto') {
      return 'Não foi possível carregar o código de verificação. Tente novamente.';
    }

    return this.captchaDigitado().trim() ? '' : 'Digite os caracteres da imagem.';
  });

 
   private readonly buscaAtual = signal('');

  readonly consultou = computed(() => this.buscaAtual() !== '');

    readonly naoEncontrado = signal(false);

  readonly rascunho = signal<Filtros>({ ...FILTROS_VAZIOS });
  readonly filtros = signal<Filtros>({ ...FILTROS_VAZIOS });

  readonly temFiltroAtivo = computed(() => {
    const { protocolo, categoria, servicos, status, dataInicial, dataFinal } = this.filtros();

    return (
      servicos.length > 0 ||
      [protocolo, categoria, status, dataInicial, dataFinal].some((valor) => valor !== '')
    );
  });

   readonly semProtocolo = computed(() => this.consultou() && !this.filtros().protocolo.trim());

  
  readonly resultados = computed<readonly Solicitacao[]>(() => {
    if (!this.consultou() || this.semProtocolo()) return [];

    const { protocolo, categoria, servicos, status, dataInicial, dataFinal } = this.filtros();
    const daApi = this.resultadoApi();
    const base = daApi ? [daApi] : this.solicitacoesService.buscarPorProtocolo(protocolo);

    return base.filter((solicitacao) => {
      if (categoria && solicitacao.categoria !== categoria) return false;
      if (servicos.length && !servicos.includes(solicitacao.servico)) return false;
      if (status && solicitacao.status !== status) return false;
      if (dataInicial && solicitacao.abertaEm < dataInicial) return false;
      if (dataFinal && solicitacao.abertaEm > dataFinal) return false;

      return true;
    });
  });

  readonly total = computed(() => this.resultados().length);
 
  readonly modoLista = computed(() => this.total() > 1);

   readonly unica = computed<Solicitacao | undefined>(() => this.resultados()[0]);

 
  readonly itensPorPagina = signal(10);

  readonly totalPaginas = computed(() =>
    Math.max(1, Math.ceil(this.total() / this.itensPorPagina()))
  );

  readonly paginaAtual = linkedSignal({
    source: () => ({ filtros: this.filtros(), porPagina: this.itensPorPagina() }),
    computation: () => 1,
  });

  readonly resultadosDaPagina = computed<readonly Solicitacao[]>(() => {
    const inicio = (this.paginaAtual() - 1) * this.itensPorPagina();

    return this.resultados().slice(inicio, inicio + this.itensPorPagina());
  });

  readonly primeiroDaPagina = computed(() =>
    this.total() === 0 ? 0 : (this.paginaAtual() - 1) * this.itensPorPagina() + 1
  );

  readonly ultimoDaPagina = computed(() =>
    Math.min(this.paginaAtual() * this.itensPorPagina(), this.total())
  );

   readonly selecionada = signal<Solicitacao | null>(null);

  readonly buscaServico = signal('');
  readonly servicosEmEscolha = signal<readonly string[]>([]);

  readonly servicosFiltrados = computed<readonly string[]>(() => {
    const termo = this.buscaServico().trim().toLowerCase();
    if (!termo) return this.servicos();

    return this.servicos().filter((servico) => servico.toLowerCase().includes(termo));
  });

  readonly servicosSelecionados = computed(() => this.rascunho().servicos);

  /** Texto do botão que reabre o modal: nome quando é um só, contagem quando são vários. */
  readonly resumoServicos = computed(() => {
    const escolhidos = this.servicosSelecionados();

    if (!escolhidos.length) return 'Escolher serviços vinculados';
    if (escolhidos.length === 1) return escolhidos[0];

    return `${escolhidos.length} serviços selecionados`;
  });

  readonly resumoEscolha = computed(() => {
    const total = this.servicosEmEscolha().length;

    if (!total) return 'Nenhum serviço marcado: todos serão considerados.';

    return total === 1 ? '1 serviço marcado.' : `${total} serviços marcados.`;
  });

 
  readonly tituloTrilha = computed(() => {
    if (!this.consultou() || this.semProtocolo()) return 'Acompanhamento de Solicitação';
    if (this.naoEncontrado()) return 'Resultado';

    return this.modoLista() ? 'Minhas solicitações' : 'Resultado';
  });

 
  readonly tituloResultado = computed(() =>
    this.semProtocolo() ? 'Acompanhamento de Solicitação' : 'Andamento da Solicitação'
  );

  readonly subtituloResultado = computed(() =>
    this.semProtocolo()
      ? 'Informe um protocolo no campo acima para consultar o andamento.'
      : 'Acompanhe o status, prazo legal e o parecer final do órgão competente.'
  );

 
  constructor() {
    this.carregarCaptcha();
  }

  aoDigitarConsulta(campo: 'protocolo' | 'captchaDigitado', evento: Event): void {
    this[campo].set((evento.target as HTMLInputElement).value);

    if (campo === 'captchaDigitado') this.erroCaptchaApi.set('');
  }

  aoDigitarCpf(evento: Event): void {
    this.cpf.set(formatarCpf((evento.target as HTMLInputElement).value));
  }

  /** Pede um captcha novo à API. O anterior, se havia, deixa de ser enviado. */
  carregarCaptcha(): void {
    this.carregamentoCaptcha?.unsubscribe();
    this.captcha.set(null);
    this.estadoCaptcha.set('carregando');

    this.carregamentoCaptcha = this.captchaConsulta
      .obter()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (captcha) => {
          this.captcha.set(captcha);
          this.estadoCaptcha.set('pronto');
        },
        error: () => this.estadoCaptcha.set('falha'),
      });
  }

  trocarCaptcha(): void {
    this.captchaDigitado.set('');
    this.erroCaptchaApi.set('');
    this.carregarCaptcha();
  }

  /** Quem não consegue ler a imagem acompanha pela conta: o foco vai ao login da própria tela. */
  focarLogin(): void {
    document.getElementById('usuario')?.focus();
  }

  /** O que a consulta leva nos cabeçalhos. Só é chamado com o captcha carregado (`erroCaptcha`). */
  private respostaCaptcha(): RespostaCaptcha {
    return { token: this.captcha()?.token ?? '', resposta: this.captchaDigitado() };
  }

  /**
   * A API recusou o código: ele foi consumido. A tela traz outro, esvazia o campo e põe o foco nele,
   * com o erro ligado ao campo — o protocolo e o CPF ficam.
   */
  private recusarCaptcha(mensagem: string): void {
    this.trocarCaptcha();
    this.erroCaptchaApi.set(mensagem);
    this.campoCaptcha()?.nativeElement.focus();
  }

  /**
   * A consulta falhou fora do 404 e da recusa do captcha. No 429 o limitador barrou antes da
   * conferência e o código continua valendo; no resto ele pode ter sido consumido, e vem outro.
   */
  private falhaNaConsulta(tipo: string, mensagem: string): void {
    this.erroConsulta.set(mensagem);

    if (tipo !== 'limite') this.trocarCaptcha();
  }

  consultar(evento: Event): void {
    evento.preventDefault();
    this.tentouConsultar.set(true);

    if (this.erroProtocolo() || this.erroCpf() || this.erroCaptcha()) {
      return;
    }

    const protocolo = this.protocolo().trim();

    if (protocolo.toUpperCase().startsWith(PREFIXO_PROTOCOLO_SERVICO)) {
      this.consultarServico(protocolo, this.respostaCaptcha());
      return;
    }

    if (protocolo.toUpperCase().startsWith(PREFIXO_PROTOCOLO_SIC)) {
      this.consultarPedidoSic(protocolo.toUpperCase(), this.respostaCaptcha());
      return;
    }

    this.resultadoApi.set(null);
    this.buscaAtual.set(protocolo);
    this.aplicar({ ...clonarFiltros(FILTROS_VAZIOS), protocolo });
    this.naoEncontrado.set(!this.total());
  }

  /**
   * Protocolo de solicitação de serviço: a consulta é na API, e o resultado entra no mesmo
   * modelo da tela. O 404 vira o "Protocolo não encontrado" de sempre; a falha de rede fica no
   * formulário, para a pessoa tentar de novo sem perder o que digitou.
   */
  private consultarServico(protocolo: string, captcha: RespostaCaptcha): void {
    if (this.consultandoApi()) return;

    this.consultandoApi.set(true);
    this.erroConsulta.set('');

    this.solicitacaoServicoPublica
      .consultar(protocolo, captcha)
      .pipe(
        finalize(() => this.consultandoApi.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (resposta) => {
          this.resultadoApi.set(paraSolicitacaoAcompanhamento(resposta));
          this.buscaAtual.set(resposta.desProtocoloSsv);
          this.aplicar({ ...clonarFiltros(FILTROS_VAZIOS), protocolo: resposta.desProtocoloSsv });
          this.naoEncontrado.set(false);
        },
        error: (erro: unknown) => {
          const tratado = mapearErroSolicitacao(erro, 'consulta');

          if (tratado.tipo === 'nao-encontrado') {
            this.resultadoApi.set(null);
            this.buscaAtual.set(protocolo);
            this.aplicar({ ...clonarFiltros(FILTROS_VAZIOS), protocolo });
            this.naoEncontrado.set(true);
            return;
          }

          if (tratado.tipo === 'captcha') {
            this.recusarCaptcha(tratado.mensagem);
            return;
          }

          this.falhaNaConsulta(tratado.tipo, tratado.mensagem);
        },
      });
  }

  /**
   * Protocolo do e-SIC: a consulta pública por protocolo, sem o token e sem o CPF. O 404 é o
   * "Protocolo não encontrado" de sempre; o limite e a indisponibilidade ficam no formulário,
   * sem a mensagem da API.
   */
  private consultarPedidoSic(protocolo: string, captcha: RespostaCaptcha): void {
    if (this.consultandoApi()) return;

    this.consultandoApi.set(true);
    this.erroConsulta.set('');

    this.pedidoSic
      .consultar(protocolo, captcha)
      .pipe(
        finalize(() => this.consultandoApi.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (resposta) => {
          this.resultadoApi.set(paraSolicitacaoSic(resposta));
          this.buscaAtual.set(resposta.protocolo);
          this.aplicar({ ...clonarFiltros(FILTROS_VAZIOS), protocolo: resposta.protocolo });
          this.naoEncontrado.set(false);
        },
        error: (erro: unknown) => {
          const tratado = mapearErroConsultaSic(erro);

          if (tratado.tipo === 'nao-encontrado') {
            this.resultadoApi.set(null);
            this.buscaAtual.set(protocolo);
            this.aplicar({ ...clonarFiltros(FILTROS_VAZIOS), protocolo });
            this.naoEncontrado.set(true);
            return;
          }

          if (tratado.tipo === 'captcha') {
            this.recusarCaptcha(tratado.mensagem);
            return;
          }

          this.falhaNaConsulta(tratado.tipo, tratado.mensagem);
        },
      });
  }

   novaConsulta(): void {
    this.resultadoApi.set(null);
    this.erroConsulta.set('');
    this.buscaAtual.set('');
    this.naoEncontrado.set(false);
    this.tentouConsultar.set(false);
    this.trocarCaptcha();
    this.aplicar(clonarFiltros(FILTROS_VAZIOS));
  }

  aoFiltrar(campo: Exclude<keyof Filtros, 'servicos'>, evento: Event): void {
    const valor = (evento.target as HTMLInputElement | HTMLSelectElement).value;
    this.rascunho.update((atual) => ({ ...atual, [campo]: valor }));
  }

  aoFiltrarCategoria(evento: Event): void {
    const categoria = (evento.target as HTMLSelectElement).value;
    this.rascunho.update((atual) => ({ ...atual, categoria, servicos: [] }));

    if (categoria === 'servico') {
      this.abrirModalServicos();
    }
  }

  abrirModalServicos(): void {
    this.buscaServico.set('');
    this.servicosEmEscolha.set([...this.rascunho().servicos]);
    this.modalServicos()?.nativeElement.showModal();
  }

  fecharModalServicos(): void {
    this.modalServicos()?.nativeElement.close();
  }

  aoBuscarServico(evento: Event): void {
    this.buscaServico.set((evento.target as HTMLInputElement).value);
  }

  servicoMarcado(servico: string): boolean {
    return this.servicosEmEscolha().includes(servico);
  }

  alternarServico(servico: string): void {
    this.servicosEmEscolha.update((atual) =>
      atual.includes(servico) ? atual.filter((item) => item !== servico) : [...atual, servico]
    );
  }

  /** Marca o que está visível na busca sem descartar o que já estava marcado fora dela. */
  marcarTodosServicos(): void {
    this.servicosEmEscolha.update((atual) => [
      ...atual,
      ...this.servicosFiltrados().filter((servico) => !atual.includes(servico)),
    ]);
  }

  recarregarServicos(): void {
    this.catalogo.recarregarServicos();
  }

  limparSelecaoServicos(): void {
    this.servicosEmEscolha.set([]);
  }

  confirmarServicos(): void {
    this.rascunho.update((atual) => ({
      ...atual,
      categoria: 'servico',
      servicos: [...this.servicosEmEscolha()],
    }));
    this.fecharModalServicos();
  }

  filtrar(): void {
    this.aplicar(this.rascunho());
  }

  limparFiltros(): void {
    this.aplicar(clonarFiltros(FILTROS_VAZIOS));
  }

  private aplicar(filtros: Filtros): void {
    this.rascunho.set(clonarFiltros(filtros));
    this.filtros.set(clonarFiltros(filtros));
  }

  irParaPagina(pagina: number): void {
    this.paginaAtual.set(pagina);
  }

  aoTrocarItensPorPagina(evento: Event): void {
    this.itensPorPagina.set(Number((evento.target as HTMLSelectElement).value));
  }

  abrirDetalhe(solicitacao: Solicitacao): void {
    this.selecionada.set(solicitacao);
    this.modalDetalhe()?.nativeElement.showModal();
  }

  fecharDetalhe(): void {
    this.modalDetalhe()?.nativeElement.close();
  }

  /** O complemento devolveu a consulta atualizada: ela substitui a da página e a do modal. */
  aoAtualizarSolicitacao(atualizada: Solicitacao): void {
    this.resultadoApi.set(atualizada);

    if (this.selecionada()?.protocolo === atualizada.protocolo) {
      this.selecionada.set(atualizada);
    }
  }

  imprimir(): void {
    window.print();
  }

  
  async baixarPdf(): Promise<void> {
    const solicitacao = this.unica();
    const origem = this.detalhePdf()?.nativeElement;
    if (!solicitacao || !origem || this.gerandoPdf()) return;

    this.gerandoPdf.set(true);

    const { esconderijo, palco } = this.montarPalco(origem, solicitacao);
    document.body.appendChild(esconderijo);

    try {
      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
        import('html2canvas'),
        import('jspdf'),
      ]);

      const captura = await html2canvas(palco, {
        scale: 2,
        backgroundColor: '#FFFFFF',
        logging: false,
        useCORS: true,
        windowWidth: LARGURA_PDF,
      });

       const doc = new jsPDF({ unit: 'pt', format: 'a4', compress: true });
      const margem = 24;
      const larguraUtil = doc.internal.pageSize.getWidth() - margem * 2;
      const alturaUtil = doc.internal.pageSize.getHeight() - margem * 2;

       const proporcao = larguraUtil / captura.width;
      const alturaDaFatia = Math.floor(alturaUtil / proporcao);

      for (let topo = 0, pagina = 0; topo < captura.height; topo += alturaDaFatia, pagina++) {
        const altura = Math.min(alturaDaFatia, captura.height - topo);

        const fatia = document.createElement('canvas');
        fatia.width = captura.width;
        fatia.height = altura;
        fatia
          .getContext('2d')
          ?.drawImage(captura, 0, topo, captura.width, altura, 0, 0, captura.width, altura);

        if (pagina) doc.addPage();
        doc.addImage(fatia, 'PNG', margem, margem, larguraUtil, altura * proporcao, '', 'FAST');
      }

      doc.save(`solicitacao-${solicitacao.protocolo}.pdf`);
    } finally {
      esconderijo.remove();
      this.gerandoPdf.set(false);
    }
  }
 
  private montarPalco(
    origem: HTMLElement,
    solicitacao: Solicitacao
  ): { esconderijo: HTMLElement; palco: HTMLElement } {
    const esconderijo = document.createElement('div');
    esconderijo.setAttribute('aria-hidden', 'true');
    Object.assign(esconderijo.style, {
      position: 'fixed',
      top: '0',
      left: `-${LARGURA_PDF * 2}px`,
      width: `${LARGURA_PDF}px`,
      pointerEvents: 'none',
    });

    const palco = document.createElement('div');
    Object.assign(palco.style, {
       boxSizing: 'border-box',
      width: `${LARGURA_PDF}px`,
      padding: '32px',
      background: '#FFFFFF',
      fontFamily: "var(--body-font-family, 'Poppins', sans-serif)",
    });

    const cabecalho = document.createElement('div');
    cabecalho.style.cssText =
      'margin-bottom:24px;padding-bottom:16px;border-bottom:1px solid #E5E7EB;';
    cabecalho.innerHTML = `
      <p style="margin:0 0 4px;color:#06334E;font-size:24px;font-weight:700;">Andamento da Solicitação</p>
      <p style="margin:0;color:#6B7280;font-size:14px;">
        Protocolo ${solicitacao.protocolo} — emitido pelo portal Carta de Serviços
      </p>`;

    const clone = origem.cloneNode(true) as HTMLElement;
      clone.style.display = 'grid';
    clone.style.gridTemplateColumns = 'minmax(0, 2fr) minmax(0, 1fr)';
    clone.style.gap = '20px';

    palco.append(cabecalho, clone);
    esconderijo.appendChild(palco);

    return { esconderijo, palco };
  }

 
  dataFormatada(iso: string): string {
    return formatarData(iso);
  }

  rotuloStatus(status: StatusSolicitacao): string {
    return this.statusDisponiveis.find((opcao) => opcao.id === status)?.rotulo ?? '';
  }

  rotuloCategoria(categoria: CategoriaSolicitacao): string {
    return this.categoriasDisponiveis.find((opcao) => opcao.id === categoria)?.rotulo ?? '';
  }

  iconeCategoria(categoria: CategoriaSolicitacao): string {
    return this.categoriasDisponiveis.find((opcao) => opcao.id === categoria)?.icone ?? 'tag';
  }
}
